// src/components/modals/ViewUnitModal.jsx
import { X } from 'lucide-react'
import UnitsService from '../../services/unitsService'

const ViewUnitModal = ({ isOpen, onClose, unit }) => {
  // Don't render if not open or no unit
  if (!isOpen || !unit) return null

  // Get type icon and color
  const typeIcon = UnitsService.getTypeIcon(unit.type)
  const typeColor = UnitsService.getTypeColor(unit.type)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Unit Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Row 1: Category name & Abbreviation */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Category name
                </label>
                <p className="text-base font-medium text-gray-900">{unit.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Abbreviation
                </label>
                <div className="flex items-center space-x-2">
                  <p className="text-base font-medium text-gray-900">{unit.abbreviation}</p>
                  <span className="text-lg">{typeIcon}</span>
                </div>
              </div>
            </div>

            {/* Row 2: Type & Status */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Type
                </label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                  ${typeColor === 'blue' ? 'bg-blue-100 text-blue-800' :
                    typeColor === 'green' ? 'bg-green-100 text-green-800' :
                    typeColor === 'purple' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'}`}
                >
                  <span className="mr-1">{typeIcon}</span>
                  {UnitsService.getTypeDisplayLabel(unit.type)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Status
                </label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  unit.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {unit.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Row 3: Created & Last Updated */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Created
                </label>
                <p className="text-base text-gray-900">
                  {unit.createdAt ? new Date(unit.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Last Updated
                </label>
                <p className="text-base text-gray-900">
                  {unit.updatedAt ? new Date(unit.updatedAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            {/* Usage Count - Single item if exists */}
            {unit.usageCount !== undefined && (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Usage Count
                  </label>
                  <p className="text-base text-gray-900">
                    Used in {unit.usageCount || 0} products
                  </p>
                </div>
                <div></div>
              </div>
            )}

            {/* Description - Full width if exists */}
            {unit.description && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Description
                </label>
                <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-md">
                  {unit.description}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewUnitModal